import React, { useState, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ItineraryModal from './ItineraryModal';

import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { bookingIncludesTherapist } from '../utils/bookings';

const ViewButton = styled(Button)(({ theme, selected }) => ({
  textTransform: 'none',
  minWidth: '120px',
  backgroundColor: selected ? '#E4C984' : 'transparent',
  color: selected ? '#171717' : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: selected ? '#cbb16f' : theme.palette.action.hover,
  },
}));

// Gold-accented outlined group (replaces default blue borders/outlines)
const GoldButtonGroup = styled(ButtonGroup)(() => ({
  '& .MuiButton-outlined': {
    borderColor: '#E4C984',
  },
  '& .MuiButton-outlined:hover': {
    borderColor: '#cbb16f',
    backgroundColor: 'rgba(228, 201, 132, 0.12)',
  },
  '& .MuiButtonBase-root.Mui-focusVisible': {
    boxShadow: '0 0 0 2px rgba(228, 201, 132, 0.6)'
  }
}));

const guestIdsMatch = (a, b) => {
  if (a == null || b == null || a === '' || b === '') return false;
  return String(a) === String(b);
};

const getBookingTimeStr = (booking, field = 'start') => {
  const raw = field === 'start'
    ? (booking.startTime ?? booking.start_time)
    : (booking.endTime ?? booking.end_time);
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string' && raw.includes(':')) {
    const [h, m] = raw.split(':').map(Number);
    if (!Number.isNaN(h) && !Number.isNaN(m)) {
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
  }
  if (typeof raw === 'number') {
    const hours = Math.floor(raw / 60);
    const mins = raw % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  }
  return String(raw);
};

const collectAssignedFromBookings = (bookingList) => {
  const guestRooms = new Set();
  const guestTherapists = new Set();
  const guestTreatments = new Set();
  bookingList.forEach((booking) => {
    if (booking.room) guestRooms.add(booking.room);
    if (booking.therapist) guestTherapists.add(booking.therapist);
    if (booking.therapists && Array.isArray(booking.therapists)) {
      booking.therapists.forEach((t) => {
        if (t.name) guestTherapists.add(t.name);
      });
    }
    if (booking.treatment) guestTreatments.add(booking.treatment);
  });
  return { guestRooms, guestTherapists, guestTreatments };
};

const sortAssignedFirst = (items, assignedSet, getKey) => {
  const assigned = [];
  const other = [];
  items.forEach((item) => {
    if (assignedSet.has(getKey(item))) assigned.push(item);
    else other.push(item);
  });
  const byKey = (a, b) => getKey(a).localeCompare(getKey(b), undefined, { sensitivity: 'base' });
  assigned.sort(byKey);
  other.sort(byKey);
  return [...assigned, ...other];
};

const CalendarPanel = ({
  selectedDate,
  onDateChange,
  dateRange,
  bookings: parentBookings,
  config,
  onEditBooking,
  isLoading: parentIsLoading,
  refreshTrigger,
  headerVariant = 'default', // 'default' | 'compact'
  headerExtras = null,
  showTitle = true,
  filterGuestId = null, // Optional: filter to show only this guest's bookings
  guestPriorityBookings = null, // Optional: guest bookings across dates for column ordering
  isExpanded = false, // Whether calendar is in expanded/fullscreen mode
  onToggleExpand = null // Callback to toggle expanded mode
}) => {
  const [viewType, setViewType] = useState('room');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('schedulerView') || 'normal');
  const [localBookings, setLocalBookings] = useState({});
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showItinerary, setShowItinerary] = useState(false);

  const fetchBookings = async (date) => {
    try {
      setIsLocalLoading(true);
      const response = await apiGet(`/api/bookings/${date}?_=${Date.now()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }
      const data = await response.json();
      setLocalBookings({
        [date]: data
      });
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setIsLocalLoading(false);
    }
  };

  // Refresh when date changes or when parent triggers a refresh
  useEffect(() => {
    if (selectedDate) {
      fetchBookings(selectedDate);
    }
  }, [selectedDate, refreshTrigger]);

  // Use either parent bookings or local bookings
  const rawBookings = useMemo(() => {
    if (parentBookings != null) {
      return Array.isArray(parentBookings) ? parentBookings : [];
    }
    const data = localBookings[selectedDate] || [];
    // Ensure it's always an array
    if (Array.isArray(data)) return data;
    // If it's an object (grouped by room/therapist), flatten it
    return Object.values(data).flat();
  }, [parentBookings, localBookings, selectedDate]);
  
  // Filter bookings by guest if filterGuestId is provided
  const bookings = useMemo(() => {
    if (!filterGuestId) return rawBookings;
    
    return rawBookings.filter(booking => {
      const bookingGuestId = booking.guestId || booking.guest_id;
      return guestIdsMatch(bookingGuestId, filterGuestId);
    });
  }, [rawBookings, filterGuestId]);

  // When a guest is selected: show only their assigned columns on the selected date
  const displayConfig = useMemo(() => {
    if (!filterGuestId) return config;

    const dateBookings = bookings;
    const sourceForColumns = dateBookings.length > 0
      ? dateBookings
      : (Array.isArray(guestPriorityBookings) ? guestPriorityBookings : []);

    const { guestRooms, guestTherapists, guestTreatments } = collectAssignedFromBookings(sourceForColumns);

    const roomEntries = sortAssignedFirst(
      Object.entries(config.rooms || {}).filter(([name]) => guestRooms.has(name)),
      guestRooms,
      ([roomName]) => roomName
    );

    const therapistEntries = sortAssignedFirst(
      Object.entries(config.therapists || {}).filter(([, therapist]) => guestTherapists.has(therapist.name)),
      guestTherapists,
      ([, therapist]) => therapist.name
    );

    const treatments = sortAssignedFirst(
      (config.treatments || []).filter((t) => guestTreatments.has(t.name)),
      guestTreatments,
      (t) => t.name
    );

    return {
      ...config,
      rooms: Object.fromEntries(roomEntries),
      therapists: Object.fromEntries(therapistEntries),
      treatments,
    };
  }, [config, filterGuestId, guestPriorityBookings, bookings]);
  
  const isLoading = parentIsLoading || isLocalLoading;

  const handlePrevDate = () => {
    const currentIndex = dateRange.indexOf(selectedDate);
    if (currentIndex > 0) {
      onDateChange(dateRange[currentIndex - 1]);
    }
  };

  const handleNextDate = () => {
    const currentIndex = dateRange.indexOf(selectedDate);
    if (currentIndex < dateRange.length - 1) {
      onDateChange(dateRange[currentIndex + 1]);
    }
  };

  const handleViewChange = (newView) => {
    console.log('View change:', newView);
    setViewType(newView);
  };

  const handleDateChange = (date) => {
    console.log('Date change:', date);
    onDateChange(date);
  };

  useEffect(() => {
    localStorage.setItem('schedulerView', viewMode);
  }, [viewMode]);

  const SLOT_MINUTES = viewMode === 'compact' ? 30 : 15;
  const SLOT_HEIGHT = viewMode === 'compact' ? 28 : 40;

  const colorForTreatment = (name) => {
    const str = String(name || '');
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    const hue = Math.abs(hash) % 360;
    const bg = `hsl(${hue}, 80%, ${viewMode === 'compact' ? 93 : 92}%)`;
    const bd = `hsl(${hue}, 60%, ${viewMode === 'compact' ? 65 : 62}%)`;
    return { bg, bd };
  };

  const calculateTimeSlots = () => {
    const slots = [];
    const dayStart = 6 * 60; // 6:00 AM
    const dayEnd = 19 * 60; // 7:00 PM

    for (let time = dayStart; time <= dayEnd; time += SLOT_MINUTES) {
      const hours = Math.floor(time / 60);
      const minutes = time % 60;
      const actualStartTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

      slots.push({
        startTime: time,
        actualStartTime,
        slotId: `slot-${time}`
      });
    }

    return slots;
  };

  const memoSlots = React.useMemo(() => calculateTimeSlots(), [SLOT_MINUTES, viewMode]);

  const renderBookings = (timeSlot, columnKey) => {
    // Simply filter by matching column and start time
    const relevantBookings = (Array.isArray(bookings) ? bookings : []).filter(booking => {
      const startStr = getBookingTimeStr(booking, 'start');
      if (!startStr) return false;

      const [bookingHours, bookingMinutes] = startStr.split(':').map(Number);
      const bookingStartTime = bookingHours * 60 + bookingMinutes;

      // Find the closest time slot for this booking
      const closestSlotTime = Math.floor(bookingStartTime / SLOT_MINUTES) * SLOT_MINUTES;
      
      let matchesColumn = false;
      switch (viewType) {
        case 'room':
          matchesColumn = booking.room === columnKey;
          break;
        case 'therapist':
          matchesColumn = bookingIncludesTherapist(booking, columnKey);
          break;
        case 'treatment':
          matchesColumn = booking.treatment === columnKey;
          break;
      }

      // Match booking to the closest slot
      return closestSlotTime === timeSlot.startTime && matchesColumn;
    });

    return relevantBookings.map(booking => {
      const startStr = getBookingTimeStr(booking, 'start');
      const endStr = getBookingTimeStr(booking, 'end');
      if (!startStr || !endStr) return null;

      const [startHours, startMinutes] = startStr.split(':').map(Number);
      const [endHours, endMinutes] = endStr.split(':').map(Number);
      const bookingStartTime = startHours * 60 + startMinutes;
      const bookingEndTime = endHours * 60 + endMinutes;
      const duration = bookingEndTime - bookingStartTime;
      
      // Calculate height based on duration
      const slots = Math.ceil(duration / SLOT_MINUTES);
      const height = slots * SLOT_HEIGHT - 2; // -2 for borders

      const status = String(booking.status || '').toLowerCase();
      const statusLabels = {
        pre_close: 'Pre-close',
        checked_in: 'Checked in',
        in_progress: 'In progress',
        done: 'Done',
      };
      const statusLabel = statusLabels[status] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : '');
      const showStatus = ['started', 'pre_close', 'closed', 'checked_in', 'in_progress', 'done'].includes(status);

      return (
        <div
          key={booking.bookingId || booking.id}
          onClick={(e) => {
            // Set selected guest for itinerary
            if (booking.guestId || booking.guestName) {
              setSelectedGuest({
                guestId: booking.guestId || booking.guest_id,
                guestName: booking.guestName || booking.guest_name
              });
            }
            // Also call the edit booking handler
            onEditBooking && onEditBooking(booking, e);
          }}
          className="absolute left-0 right-0 p-1 overflow-hidden cursor-pointer z-[5] text-left font-sans"
          style={{
            height: `${height}px`,
            marginTop: `-${Math.floor(SLOT_HEIGHT * 0.45)}px`,
            backgroundColor: colorForTreatment(booking.treatment).bg,
            border: `1px solid ${colorForTreatment(booking.treatment).bd}`
          }}
        >
          <div className="flex items-center justify-between gap-1">
            <div className="flex-1 min-w-0">{renderBookingContent(booking)}</div>
            {showStatus && (
              <span className="shrink-0 text-[10px] px-1 py-0.5 rounded-full" style={{ border: '1px solid #E4C984', backgroundColor: 'rgba(228, 201, 132, 0.18)', color: '#171717' }}>{statusLabel}</span>
            )}
          </div>
        </div>
      );
    });
  };

  // Helper to format therapist display for multi-therapist bookings
  const formatTherapistDisplay = (booking, compact = false) => {
    const therapists = booking.therapists || [];
    const therapistsCount = booking.therapistsCount || (therapists.length > 0 ? therapists.length : 1);
    
    if (therapistsCount === 1 || therapists.length === 0) {
      // Single therapist (backward compatibility)
      return booking.therapist || 'N/A';
    }
    
    // Multiple therapists
    const primary = therapists.find(t => t.is_primary);
    const primaryName = primary ? primary.name : (therapists[0]?.name || booking.therapist);
    
    if (compact) {
      // Compact view: "FirstName +(N)"
      const firstName = primaryName.split(' ')[0];
      return `${firstName} +(${therapistsCount - 1})`;
    }
    
    // Full view: try to show all names if they fit
    const allNames = therapists.map(t => t.name).join(', ');
    if (allNames.length < 40) {
      return allNames;
    } else {
      // Too long, use compact format
      const firstName = primaryName.split(' ')[0];
      return `${firstName} +(${therapistsCount - 1})`;
    }
  };

  const renderBookingContent = (booking) => {
    const therapistsCount = booking.therapistsCount || 1;
    const isMultiTherapist = therapistsCount > 1;
    
    switch (viewType) {
      case 'room':
        return (
          <>
            <div className="text-[11px] font-medium truncate">{booking.treatment}</div>
            <div className="text-[10px] text-gray-600 truncate">
              {booking.guestName} | {formatTherapistDisplay(booking, viewMode === 'compact')}
            </div>
            {isMultiTherapist && (
              <div className="text-[9px] text-blue-600">👥 {therapistsCount} therapists</div>
            )}
            {booking.id && <div className="text-[9px] text-gray-500">ID: {booking.id}</div>}
          </>
        );
      case 'therapist':
        return (
          <>
            {viewMode === 'compact' ? (
              <div className="text-[11px] font-medium truncate">{booking.treatment}</div>
            ) : (
              <>
                <div className="text-[11px] font-medium truncate">{booking.treatment}</div>
                <div className="text-[10px] text-gray-600 truncate">{booking.guestName} | Room {booking.room}</div>
                {isMultiTherapist && (
                  <div className="text-[9px] text-blue-600">👥 {therapistsCount} therapists</div>
                )}
              </>
            )}
            {booking.id && <div className="text-[9px] text-gray-500">ID: {booking.id}</div>}
          </>
        );
      case 'treatment':
        return (
          <>
            {viewMode === 'compact' ? (
              <div className="text-[11px] font-medium truncate">{formatTherapistDisplay(booking, true) || booking.room}</div>
            ) : (
              <>
                <div className="text-[11px] font-medium truncate">{booking.treatment}</div>
                <div className="text-[10px] text-gray-600 truncate">
                  {booking.guestName} | {formatTherapistDisplay(booking)}
                </div>
                {isMultiTherapist && (
                  <div className="text-[9px] text-blue-600">👥 {therapistsCount} therapists</div>
                )}
              </>
            )}
            {booking.id && <div className="text-[9px] text-gray-500">ID: {booking.id}</div>}
          </>
        );
      default:
        return null;
    }
  };

  const activeConfig = filterGuestId ? displayConfig : config;

  const renderRoomView = () => {
    const roomsToShow = activeConfig.rooms;
    
    if (Object.keys(roomsToShow || {}).length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <p className="text-lg font-medium">No bookings found</p>
            <p className="text-sm">This guest has no bookings on the selected date</p>
          </div>
        </div>
      );
    }
    
    return (
      <table className="w-full border-collapse relative">
        <thead className="sticky top-0 bg-white z-20">
          <tr>
            <th className="sticky left-0 bg-gray-50 z-[15] p-1 border">Time</th>
            {Object.entries(roomsToShow || {}).map(([roomName, room]) => (
              <th key={roomName} className="p-1 border bg-gray-50 min-w-[250px]">
                <div className="font-bold">{roomName}</div>
                <div className="text-xs text-gray-500">({room.tier})</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {memoSlots.map((timeSlot) => (
            <tr key={timeSlot.actualStartTime} style={{ height: `${SLOT_HEIGHT}px` }}>
              <td className="sticky left-0 bg-white z-[15] p-1 border text-sm">
                {timeSlot.actualStartTime}
              </td>
              {Object.keys(roomsToShow || {}).map((roomName) => (
                <td
                  key={`${timeSlot.actualStartTime}-${roomName}`}
                  className="p-1 border relative"
                >
                  {renderBookings(timeSlot, roomName)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderTherapistView = () => {
    const therapistsToShow = activeConfig.therapists;
    
    if (Object.keys(therapistsToShow || {}).length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <p className="text-lg font-medium">No bookings found</p>
            <p className="text-sm">This guest has no bookings on the selected date</p>
          </div>
        </div>
      );
    }
    
    return (
      <table className="w-full border-collapse relative">
        <thead className="sticky top-0 bg-white z-20">
          <tr>
            <th className="sticky left-0 bg-gray-50 z-[15] p-1 border">Time</th>
            {Object.values(therapistsToShow || {}).map((therapist) => (
              <th key={therapist.name} className="p-1 border bg-gray-50 min-w-[250px]">
                <div className="font-bold">{therapist.name}</div>
                <div className="text-xs text-gray-500">Rank {therapist.rank}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {memoSlots.map((timeSlot) => (
            <tr key={timeSlot.actualStartTime} style={{ height: `${SLOT_HEIGHT}px` }}>
              <td className="sticky left-0 bg-white z-[15] p-1 border text-sm">
                {timeSlot.actualStartTime}
              </td>
              {Object.values(therapistsToShow || {}).map((therapist) => (
                <td
                  key={`${timeSlot.actualStartTime}-${therapist.name}`}
                  className="p-1 border relative"
                >
                  {renderBookings(timeSlot, therapist.name)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderTreatmentView = () => {
    const treatments = activeConfig.treatments || [];
    
    if (treatments.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <p className="text-lg font-medium">No bookings found</p>
            <p className="text-sm">This guest has no bookings on the selected date</p>
          </div>
        </div>
      );
    }
    
    return (
      <table className="w-full border-collapse relative">
        <thead className="sticky top-0 bg-white z-20">
          <tr>
            <th className="sticky left-0 bg-gray-50 z-[15] p-1 border">Time</th>
            {treatments.map((treatment) => (
              <th key={treatment.name} className="p-1 border bg-gray-50 min-w-[250px]">
                <div className="font-bold">{treatment.name}</div>
                <div className="text-xs text-gray-500">{treatment.duration} min</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {memoSlots.map((timeSlot) => (
            <tr key={timeSlot.actualStartTime} style={{ height: `${SLOT_HEIGHT}px` }}>
              <td className="sticky left-0 bg-white z-[15] p-1 border text-sm">
                {timeSlot.actualStartTime}
              </td>
              {treatments.map((treatment) => (
                <td
                  key={`${timeSlot.actualStartTime}-${treatment.name}`}
                  className="p-1 border relative"
                >
                  {renderBookings(timeSlot, treatment.name)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="flex-1 bg-white shadow-lg m-4 rounded-lg">
      <div className="p-4">
        {/* Header */}
        {headerVariant === 'default' ? (
          <>
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white z-20">
              {showTitle && (<h3 className="text-lg font-semibold" style={{ color: '#171717' }}>Schedule</h3>)}
              <button onClick={handlePrevDate} disabled={dateRange.indexOf(selectedDate) === 0} className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50">
                <ArrowBackIosNewIcon />
              </button>
              <h2 className="text-xl font-semibold">
                {new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </h2>
              <button onClick={handleNextDate} disabled={dateRange.indexOf(selectedDate) === dateRange.length - 1} className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50">
                <ArrowForwardIosIcon />
              </button>
            </div>
            <div className="mb-4 flex justify-between items-center">
              <GoldButtonGroup variant="outlined" size="small">
                <ViewButton selected={viewType === 'room'} onClick={() => handleViewChange('room')} startIcon={<MeetingRoomOutlinedIcon />}>Room</ViewButton>
                <ViewButton selected={viewType === 'therapist'} onClick={() => handleViewChange('therapist')} startIcon={<PersonOutlineIcon />}>Therapist</ViewButton>
                <ViewButton selected={viewType === 'treatment'} onClick={() => handleViewChange('treatment')} startIcon={<SpaOutlinedIcon />}>Treatment</ViewButton>
              </GoldButtonGroup>
              <div className="flex gap-2 items-center">
                {selectedGuest && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ListAltIcon />}
                    onClick={() => setShowItinerary(true)}
                    sx={{
                      borderColor: '#E4C984',
                      color: '#171717',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#d4b974',
                        backgroundColor: 'rgba(228, 201, 132, 0.08)'
                      }
                    }}
                  >
                    View Itinerary: {selectedGuest.guestName || selectedGuest.guestId}
                  </Button>
                )}
                <GoldButtonGroup variant="outlined" size="small">
                  <ViewButton selected={viewMode === 'normal'} onClick={() => setViewMode('normal')}>Normal</ViewButton>
                  <ViewButton selected={viewMode === 'compact'} onClick={() => setViewMode('compact')}>Compact</ViewButton>
                </GoldButtonGroup>
              </div>
            </div>
          </>
        ) : (
          <div className="mb-3 flex justify-between items-center">
            <div className="flex items-center gap-2 min-w-0">
              {showTitle && (<h3 className="text-lg font-semibold shrink-0" style={{ color: '#171717' }}>Schedule</h3>)}
              {headerExtras}
            </div>
            <div className="flex items-center gap-2">
              {selectedGuest && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ListAltIcon />}
                  onClick={() => setShowItinerary(true)}
                  sx={{
                    borderColor: '#E4C984',
                    color: '#171717',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#d4b974',
                      backgroundColor: 'rgba(228, 201, 132, 0.08)'
                    }
                  }}
                >
                  Itinerary: {selectedGuest.guestName || selectedGuest.guestId}
                </Button>
              )}
              <GoldButtonGroup variant="outlined" size="small">
                <ViewButton selected={viewType === 'room'} onClick={() => handleViewChange('room')} startIcon={<MeetingRoomOutlinedIcon />}>Room</ViewButton>
                <ViewButton selected={viewType === 'therapist'} onClick={() => handleViewChange('therapist')} startIcon={<PersonOutlineIcon />}>Therapist</ViewButton>
                <ViewButton selected={viewType === 'treatment'} onClick={() => handleViewChange('treatment')} startIcon={<SpaOutlinedIcon />}>Treatment</ViewButton>
              </GoldButtonGroup>
              <GoldButtonGroup variant="outlined" size="small">
                <ViewButton selected={viewMode === 'normal'} onClick={() => setViewMode('normal')}>Normal</ViewButton>
                <ViewButton selected={viewMode === 'compact'} onClick={() => setViewMode('compact')}>Compact</ViewButton>
              </GoldButtonGroup>
            </div>
          </div>
        )}

        {/* Date Selection Panel */}
        <div className="mb-6 border rounded-lg p-4 bg-white shadow">
          <div className="flex items-center gap-3">
            {/* Date Picker */}
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className="border rounded px-2 py-1 w-40 shrink-0 focus:outline-none focus:ring-2"
              style={{
                outlineColor: '#E4C984',
                boxShadow: '0 0 0 0px transparent',
                borderColor: undefined
              }}
              onFocus={(e) => { e.currentTarget.style.boxShadow = '0 0 0 2px #E4C984'; e.currentTarget.style.borderColor = '#E4C984'; }}
              onBlur={(e) => { e.currentTarget.style.boxShadow = '0 0 0 0px transparent'; e.currentTarget.style.borderColor = ''; }}
            />
            
            {/* Scrollable Date Buttons Container */}
            <Box 
              sx={{
                flex: 1,
                minWidth: 0,
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
                scrollbarColor: '#E4C984 #f3f4f6',
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f3f4f6',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#E4C984',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#cbb16f',
                  }
                }
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, pb: 0.5 }}>
                {dateRange.map(date => {
                  const currentDate = new Date().toISOString().split('T')[0];
                  // Show button only if date is >= current date
                  if (date >= currentDate) {
                    return (
                      <button
                        key={date}
                        onClick={() => handleDateChange(date)}
                        className={`shrink-0 px-3 py-1.5 rounded whitespace-nowrap text-sm font-medium transition-colors ${
                          date === selectedDate
                            ? "text-[#171717] shadow-sm"
                            : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                        }`}
                        style={date === selectedDate ? { backgroundColor: '#E4C984' } : undefined}
                      >
                        {new Date(date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </button>
                    );
                  }
                  return null;
                })}
              </Box>
            </Box>
            
            {/* Navigation Arrows */}
            <div className="flex items-center gap-1 shrink-0">
              <IconButton 
                onClick={handlePrevDate} 
                disabled={dateRange.indexOf(selectedDate) === 0}
                size="small"
                sx={{ 
                  '&:hover': { backgroundColor: 'rgba(228, 201, 132, 0.2)' },
                  '&.Mui-disabled': { opacity: 0.3 }
                }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>
              <IconButton 
                onClick={handleNextDate} 
                disabled={dateRange.indexOf(selectedDate) === dateRange.length - 1}
                size="small"
                sx={{ 
                  '&:hover': { backgroundColor: 'rgba(228, 201, 132, 0.2)' },
                  '&.Mui-disabled': { opacity: 0.3 }
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
              
              {/* Expand/Collapse Button */}
              {onToggleExpand && (
                <Tooltip title={isExpanded ? "Exit fullscreen" : "Expand calendar"}>
                  <IconButton 
                    onClick={onToggleExpand}
                    size="small"
                    sx={{ 
                      ml: 1,
                      color: '#E4C984',
                      border: '1px solid #E4C984',
                      '&:hover': { 
                        backgroundColor: 'rgba(228, 201, 132, 0.2)',
                        borderColor: '#cbb16f'
                      }
                    }}
                  >
                    {isExpanded ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* Calendar Content */}
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2" style={{ borderTopColor: '#C7AE6E', borderBottomColor: '#C7AE6E' }}></div>
          </div>
        ) : (
          <div className="overflow-auto max-h-[calc(100vh-300px)]">
            {viewType === 'room' && renderRoomView()}
            {viewType === 'therapist' && renderTherapistView()}
            {viewType === 'treatment' && renderTreatmentView()}
          </div>
        )}
      </div>

      {/* Itinerary Modal */}
      <ItineraryModal
        open={showItinerary}
        onClose={() => setShowItinerary(false)}
        guestId={selectedGuest?.guestId}
        guestName={selectedGuest?.guestName}
        bookingId={selectedGuest?.bookingId || selectedGuest?.booking_id}
      />
    </div>
  );
};

export default CalendarPanel;
